// Unassigned leads, in words (docs/lead-relay.md §7.1, 2026-10-07). Pure.
//
// ⚠️ ONE SOURCE FOR THE WORDING. The relay's alert, the Lead Log row and the
// detail panel all describe an unassigned lead from the SAME function over the
// SAME reason, so the three cannot drift into describing it differently.
//
// ⚠️ The relay knows less than the Lead Log: it does not read accounts, so it
// cannot tell an account with no route yet from an id that matches no account
// at all. It reports `no_active_route` and says outright that the Lead Log
// knows which; the Lead Log refines it (`unassignedReasonOf`).

import type {
  LeadAssertedAccount,
  LeadHandAssignment,
  LeadPayloadProblem,
  LeadProvider,
  LeadRoute,
  LeadRouting,
  LeadSubmission,
  LeadUnassignedReason,
} from '../../types/leadRelay.js';
import { systemName } from './providerVocabulary.js';
import { describeRouteSuggestion } from './unroutable.js';

/** Every payload problem — each means there is NO LEAD TO SEND. A Record, so a new one must be listed. */
const PAYLOAD_PROBLEMS: Record<LeadPayloadProblem, true> = {
  no_rawdata: true,
  rawdata_unreadable: true,
  ambiguous_rawdata: true,
};

/** True when the reason leaves nothing to send — Assign and send cannot help; key it in by hand. */
export function isPayloadProblem(reason: LeadUnassignedReason): reason is LeadPayloadProblem {
  return Object.prototype.hasOwnProperty.call(PAYLOAD_PROBLEMS, reason);
}

/** Every reason, exhaustive — the type is a Record key, so a new one needs wording here. */
export const UNASSIGNED_REASON_LABELS: Record<LeadUnassignedReason, string> = {
  no_rawdata: 'No Rawdata sent',
  rawdata_unreadable: 'Rawdata could not be read',
  ambiguous_rawdata: 'More than one rawdata key',
  ambiguous_account_id: 'More than one account_id key',
  no_account_id: 'No account_id sent',
  unknown_account: 'account_id matches no account',
  no_active_route: 'No active route on the account',
  several_active_routes: 'Several active routes on the account',
};

/**
 * What the relay recorded → the reason a person reads. ⚠️ A PAYLOAD problem
 * wins: with no usable Rawdata there is no lead to send, whatever the
 * account_id says. `accountExists`: whether the asserted account is a real
 * Kyck account — null when the caller cannot know (the relay).
 */
export function unassignedReasonOf(
  sub: { payloadProblem: LeadPayloadProblem | null; assertedAccount?: LeadAssertedAccount },
  accountExists: boolean | null,
): LeadUnassignedReason {
  if (sub.payloadProblem !== null) return sub.payloadProblem;
  const asserted = sub.assertedAccount;
  if (asserted?.status === 'ambiguous_account_id') return 'ambiguous_account_id';
  if (!asserted || asserted.accountId === null || asserted.status === 'no_account_id') return 'no_account_id';
  if (asserted.status === 'several_active_routes') return 'several_active_routes';
  // `routed` cannot be an unassigned lead's status; if a hand edit makes it so,
  // the safe reading is "this account needs looking at", not a route.
  return accountExists === false ? 'unknown_account' : 'no_active_route';
}

export interface UnassignedContext {
  accountId: string | null;
  /** ambiguous_account_id: the keys as they arrived. */
  ambiguousKeys?: readonly string[];
  accountName: string | null;
  /** The active routes on the account (several_active_routes). */
  activeRouteNames: string[];
  /** Inactive routes on the account — the likely fix for no_active_route. */
  inactiveRouteNames: string[];
  /** False when the caller cannot read accounts (the relay). */
  accountChecked: boolean;
}

/** Why the lead is unassigned, and the fix. */
export function describeUnassigned(reason: LeadUnassignedReason, ctx: UnassignedContext): string {
  const id = ctx.accountId ?? '';
  switch (reason) {
    case 'no_rawdata':
      return 'The Zap sent no `rawdata` field, so there is no lead to send — only the raw body below. Check the Zap sends rawdata, then key this one in by hand.';
    case 'rawdata_unreadable':
      return "The Zap's `rawdata` could not be read as a Meta lead, so there is no lead to send — only the raw body below. Key it in by hand from that.";
    case 'ambiguous_rawdata':
      return 'The Zap sent more than one rawdata field (the same name in different case), so the relay will not guess which is the lead — there is no lead to send. Fix the Zap so it sends exactly one rawdata, then key this one in by hand from the raw body below.';
    case 'ambiguous_account_id': {
      const keys = ctx.ambiguousKeys && ctx.ambiguousKeys.length > 0 ? ` (${ctx.ambiguousKeys.join(', ')})` : '';
      return `The Zap sent more than one account_id field${keys} — the same name in different case — so the relay will not guess which location this lead belongs to. Fix the Zap so it sends exactly one account_id; this lead can still be assigned.`;
    }
    case 'no_account_id':
      return 'The Zap sent no account_id, so the relay cannot tell which location this lead belongs to. Check the Zap sends account_id.';
    case 'unknown_account':
      return `The Zap's account_id "${id}" matches no account in Kyck — most likely a mistyped or stale id in the Zap.`;
    case 'no_active_route': {
      const who = ctx.accountName ? `${ctx.accountName} (account_id "${id}")` : `account_id "${id}"`;
      const inactive =
        ctx.inactiveRouteNames.length > 0
          ? ` Its route${ctx.inactiveRouteNames.length === 1 ? '' : 's'} ${ctx.inactiveRouteNames.join(', ')} ${ctx.inactiveRouteNames.length === 1 ? 'is' : 'are'} inactive.`
          : '';
      const unchecked = ctx.accountChecked
        ? ''
        : ' (The relay does not read accounts, so it cannot tell an account with no route yet from a mistyped id — the Lead Log says which.)';
      return `${who} has no active route.${inactive}${unchecked}`;
    }
    case 'several_active_routes':
      return `account_id "${id}" has ${ctx.activeRouteNames.length} active routes (${ctx.activeRouteNames.join(', ')}), so the relay will not guess between them. One active route per account is the intended shape — deactivate all but one.`;
    default: {
      const never: never = reason;
      return never;
    }
  }
}

/** The relay's alert body for an unassigned lead: the reason, then the suggestion, labelled. */
export function unassignedAlertDetail(
  sub: { payloadProblem: LeadPayloadProblem | null; assertedAccount?: LeadAssertedAccount; rawdata?: { error: string | null } },
  inactiveRouteNames: string[],
  suggestionText: string,
): string {
  const reason = unassignedReasonOf(sub, null);
  const asserted = sub.assertedAccount;
  const why = describeUnassigned(reason, {
    accountId: asserted?.accountId ?? null,
    ...(asserted?.ambiguousKeys ? { ambiguousKeys: asserted.ambiguousKeys } : {}),
    accountName: null,
    activeRouteNames: (asserted?.activeRoutes ?? []).map((r) => r.name),
    inactiveRouteNames,
    accountChecked: false,
  });
  if (sub.payloadProblem !== null) {
    const error = sub.rawdata?.error ? ` (${sub.rawdata.error})` : '';
    return `${why}${error} It has NOT been sent anywhere.`;
  }
  return `${why} ${suggestionText} It has NOT been sent anywhere — assign it from the Lead Log.`;
}

/** How a routed lead got its route, for the detail panel. Null while unassigned. */
export function describeRouting(sub: {
  status: string;
  routing?: LeadRouting;
  routeId: string | null;
  assertedAccount?: LeadAssertedAccount;
  assignment?: LeadHandAssignment;
}): string | null {
  if (sub.status === 'unassigned') return null;
  if (sub.assignment) {
    const a = sub.assignment;
    const suggested = describeRouteSuggestion(a.suggestion);
    // ⚠️ A provider a PERSON asserted (the picker) is said, never left implicit.
    const asserted = a.assertedProvider
      ? ` The system could not tell which business this lead was for; ${a.byEmail} said it was for ${systemName(a.assertedProvider)}.`
      : '';
    return `Assigned by hand by ${a.byEmail} to ${a.routeName}.${asserted} At the time: ${suggested}${a.followedSuggestion ? ' The chosen route followed it.' : ' The chosen route was not one it named.'}`;
  }
  if (sub.routing?.by === 'account_id') {
    return `Routed automatically: account_id "${sub.assertedAccount?.accountId ?? ''}" has exactly one active route.`;
  }
  return null;
}

// ── which routes a person may Assign and send to (2026-10-07) ────────────────
//
// ⚠️⚠️ THE LAST CROSS-PROVIDER HOLE. Automatic routing can no longer send an
// Anytime Fitness lead to a VirtualYard dealer (account_id is the only matcher).
// The manual path could: `choices` was every active, non-test route in the
// system, so the unassigned Roxburgh Park gym enquiry could be hand-assigned to
// BYD Liverpool — composing a VirtualYard payload from a gym enquiry and posting
// a prospective gym member to a car dealership.
//
// ONE function decides it, and BOTH sides read it: the Lead Log's list
// (crmService buildAssignmentView) and the relay's refusal (service.ts
// assignAndSend). The UI is a convenience; the server is the rule. They cannot
// disagree because neither has a rule of its own.
//
//   1. The ASSERTED ACCOUNT, where there is one, scopes the candidates.
//   2. The lead's PROVIDER must match the route's, whatever the account.
//   3. Active and not in test mode (assigning to a test-mode route would assign
//      without sending, which this action must never do).
//
// ⚠️ An unassigned lead was never composed, so it usually has no stored
// provider. It is read from the routes on its asserted account — if they all
// share ONE provider, that is the lead's. Anything else (no account, no route
// on it, routes of two providers) is UNKNOWN, and an unknown provider offers
// NOTHING: wrongly refused is visible and gets fixed; wrongly offered sends a
// member of the public's details to the wrong kind of business.

export type AssignChoicesEmptyReason =
  /** The asserted account has no route at all. */
  | 'no_route_on_account'
  /** The lead's provider cannot be worked out (no account, or its routes disagree). */
  | 'provider_unknown'
  /** Routes exist on scope, but none of this lead's provider. */
  | 'no_provider_match'
  /** Matching routes exist, but none is active and out of test mode. */
  | 'none_sendable';

type AssignRouteShape = Pick<LeadRoute, 'id' | 'name' | 'accountId' | 'active' | 'testMode'> & {
  config: { provider: LeadProvider };
};

export interface AssignableRoutes<R extends AssignRouteShape> {
  provider: LeadProvider | null;
  routes: R[];
  emptyReason: AssignChoicesEmptyReason | null;
}

/** The lead's provider: stored if it has one, else the one provider its asserted account's routes share. */
export function leadProviderForAssignment(
  sub: Pick<LeadSubmission, 'provider' | 'assertedAccount'>,
  routes: readonly AssignRouteShape[],
): LeadProvider | null {
  if (sub.provider) return sub.provider;
  const accountId = sub.assertedAccount?.accountId ?? null;
  if (accountId === null) return null;
  const providers = new Set(routes.filter((r) => r.accountId === accountId).map((r) => r.config.provider));
  return providers.size === 1 ? [...providers][0]! : null;
}

/**
 * ⚠️⚠️ THE PROVIDER PICKER (2026-10-07). A lead whose provider CANNOT be known
 * (above all `no_account_id` — the Zap broke) would otherwise be keyable by hand
 * only. The operator may ASSERT its provider; only then are that provider's
 * routes offered, scoped exactly as above. ⚠️ An assertion is consulted ONLY
 * when the provider is unknown: it can never override a KNOWN provider, which is
 * the hole this whole rule closed — and the relay REFUSES an assertion sent for
 * a lead whose provider is known (assignRouteRefusal), rather than ignoring it.
 */
export function assignableRoutes<R extends AssignRouteShape>(
  sub: Pick<LeadSubmission, 'provider' | 'assertedAccount'>,
  routes: readonly R[],
  assertedProvider: LeadProvider | null = null,
): AssignableRoutes<R> {
  const accountId = sub.assertedAccount?.accountId ?? null;
  const scoped = accountId === null ? [...routes] : routes.filter((r) => r.accountId === accountId);
  const provider = leadProviderForAssignment(sub, routes) ?? assertedProvider;
  const empty = (emptyReason: AssignChoicesEmptyReason): AssignableRoutes<R> => ({ provider, routes: [], emptyReason });
  if (accountId !== null && scoped.length === 0) return empty('no_route_on_account');
  if (provider === null) return empty('provider_unknown');
  const matching = scoped.filter((r) => r.config.provider === provider);
  if (matching.length === 0) return empty('no_provider_match');
  const sendable = matching.filter((r) => r.active && !r.testMode);
  if (sendable.length === 0) return empty('none_sendable');
  return { provider, routes: sendable, emptyReason: null };
}

/** Why the list is empty, in words — never "no route can take it" with no reason. */
export function describeEmptyAssignChoices(reason: AssignChoicesEmptyReason, provider: LeadProvider | null): string {
  const system = systemName(provider);
  switch (reason) {
    case 'no_route_on_account':
      return 'No route can take it: its account has no route. Create the route on that account first, or key it in by hand.';
    case 'provider_unknown':
      return "No route can take it: the relay cannot tell which system this lead belongs to — it has no account, or its account's routes send to different systems. Key it in by hand.";
    case 'no_provider_match':
      return `No route can take it: no route here sends to ${system}, and a lead is never sent to a different kind of business.`;
    case 'none_sendable':
      return `No route can take it right now: its ${system} route is inactive or in test mode. Activate it (out of test mode), then assign.`;
    default:
      return assertNeverReason(reason);
  }
}

function assertNeverReason(value: never): never {
  throw new Error(`Unhandled empty-choices reason: ${JSON.stringify(value)}`);
}

/**
 * The relay's refusal for a chosen route on PROVIDER or ACCOUNT grounds; null
 * when it passes both. Active / test mode are refused separately, with their own
 * messages. Together they are exactly the set assignableRoutes offers.
 */
export function assignRouteRefusal(
  sub: Pick<LeadSubmission, 'provider' | 'assertedAccount'>,
  route: AssignRouteShape,
  routes: readonly AssignRouteShape[],
  assertedProvider: LeadProvider | null = null,
): string | null {
  const known = leadProviderForAssignment(sub, routes);
  // ⚠️ A KNOWN provider is never overridden — refused, not silently ignored, so
  // a client that sends one for such a lead learns it is wrong.
  if (known !== null && assertedProvider !== null) {
    return `This lead's system is already known (${systemName(known)}), so it cannot be asserted by hand.`;
  }
  const provider = known ?? assertedProvider;
  if (provider === null) return describeEmptyAssignChoices('provider_unknown', null);
  if (route.config.provider !== provider) {
    return `${route.name} sends to ${systemName(route.config.provider)}, and this is a ${systemName(provider)} lead. A lead is never sent to a different kind of business.`;
  }
  const accountId = sub.assertedAccount?.accountId ?? null;
  if (accountId !== null && route.accountId !== accountId) {
    return `${route.name} is not on this lead's account, so it cannot take it.`;
  }
  return null;
}

/**
 * What the panel offers once a person asserts a provider — one entry per
 * provider, each scoped exactly as assignableRoutes scopes it. NULL when the
 * provider is known, or when the list is empty for another reason (no route on
 * the account): ⚠️ the picker appears ONLY for `provider_unknown`.
 */
export function providerAssertionChoices<R extends AssignRouteShape>(
  sub: Pick<LeadSubmission, 'provider' | 'assertedAccount'>,
  routes: readonly R[],
  providers: readonly LeadProvider[],
): Array<{ provider: LeadProvider; routes: R[]; emptyReason: AssignChoicesEmptyReason | null }> | null {
  if (assignableRoutes(sub, routes).emptyReason !== 'provider_unknown') return null;
  return providers.map((provider) => {
    const allowed = assignableRoutes(sub, routes, provider);
    return { provider, routes: allowed.routes, emptyReason: allowed.emptyReason };
  });
}
